import { ThemeEvents } from '@theme/events';
import { setHeaderMenuStyle, getHwBlockNavMediaQuery } from '@theme/utilities';

/**
 * HW Header Menu 2 — dropdown underlay/metrics, mega gap bridge, hover timers.
 * Dropdown position is CSS-only: absolute on the nav <li> (inset-block-start: 100%).
 */

/** @type {number | undefined} */
let closeTimer;

/** @type {number | undefined} */
let openTimer;

const HEADER_MENU_SELECTOR = 'header-menu[data-hydration-key="header-menu"]';

/** Selectors that keep the active submenu open (cancel close delay). */
const HOVER_KEEP_OPEN_SELECTORS = [
  '.hw-dropdown-submenu[data-active]',
  '.menu-list__submenu[data-submenu-type="mega_menu"][data-mega-target]',
  '.menu-list__submenu[data-submenu-type="page_showcase"][data-mega-target]',
  '.hw-dropdown-menu-2',
  '[data-hw-dropdown-flyout]',
].join(', ');

/**
 * @param {HTMLElement} headerMenu
 * @returns {{ openMs: number, closeMs: number }}
 */
function getMenuDelays(headerMenu) {
  const style = getComputedStyle(headerMenu);
  const closeMs = parseFloat(style.getPropertyValue('--hw-menu-close-delay')) || 600;
  const openMs = parseFloat(style.getPropertyValue('--hw-menu-open-delay')) || 0;

  return { openMs, closeMs };
}

function cancelCloseTimer() {
  clearTimeout(closeTimer);
}

function cancelOpenTimer() {
  clearTimeout(openTimer);
}

/**
 * @param {HTMLElement} headerMenu
 * @param {() => void} fn
 */
function scheduleClose(headerMenu, fn) {
  cancelCloseTimer();
  const { closeMs } = getMenuDelays(headerMenu);
  closeTimer = window.setTimeout(fn, closeMs);
}

/**
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findListItemFromEventTarget(element) {
  if (!(element instanceof Element)) return null;

  const link =
    element.matches('[ref="menuitem"]') ?
      element
    : element.querySelector('[ref="menuitem"]');

  return (
    link?.closest('.menu-list__list-item') ??
    element.closest('.menu-list__list-item')
  );
}

/**
 * @param {HTMLElement} li
 * @returns {HTMLElement | null}
 */
function getDropdownSubmenu(li) {
  const submenu = li.querySelector(
    '.hw-nav-item__anchor .hw-dropdown-submenu[data-submenu-type="dropdown"], .hw-dropdown-submenu[data-submenu-type="dropdown"]'
  );
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Dropdown panels sit under the link via CSS — do not expand header underlay stack.
 *
 * @param {HTMLElement} headerMenu
 * @param {HTMLElement} [submenu]
 */
function normalizeDropdownPanel(headerMenu, submenu) {
  const host =
    headerMenu.closest('header-component') ??
    document.querySelector('#header-component');

  if (!(host instanceof HTMLElement)) return;

  host.style.setProperty('--submenu-height', '0px');
  host.style.setProperty('--full-open-header-height', '0px');
  headerMenu.style.setProperty('--submenu-opacity', '1');

  if (!(submenu instanceof HTMLElement)) return;

  submenu.style.removeProperty('top');
  submenu.style.removeProperty('left');
  submenu.style.removeProperty('--hw-dropdown-top');
  submenu.style.removeProperty('--hw-dropdown-left');

  const inner = submenu.querySelector('.menu-list__submenu-inner');
  if (inner instanceof HTMLElement) {
    inner.style.setProperty('transform', 'none');
    inner.style.removeProperty('translate');
  }
}

/**
 * @param {HTMLElement} headerMenu
 */
function setupHoverCoordinator(headerMenu) {
  headerMenu.addEventListener(
    'pointerenter',
    (event) => {
      if (!(event.target instanceof Element)) return;

      if (event.target.closest(HOVER_KEEP_OPEN_SELECTORS)) {
        cancelCloseTimer();
      }

      const megaSubmenu = event.target.closest(
        '.menu-list__submenu[data-submenu-type="mega_menu"][data-mega-target], .menu-list__submenu[data-submenu-type="page_showcase"][data-mega-target]'
      );
      if (!megaSubmenu) return;

      cancelCloseTimer();

      const li = megaSubmenu.closest('.menu-list__list-item');
      if (li) {
        li.dispatchEvent(new PointerEvent('pointerenter', { bubbles: true }));
      }
    },
    true
  );
}

/**
 * @param {HTMLElement} headerMenu
 * @returns {boolean}
 */
function patchHeaderMenu(headerMenu) {
  if (headerMenu.__hw2MenuPatched) return true;
  if (typeof headerMenu.activate !== 'function') return false;

  headerMenu.__hw2MenuPatched = true;

  const originalDeactivate = headerMenu.deactivate.bind(headerMenu);
  const originalActivate = headerMenu.activate.bind(headerMenu);

  setupHoverCoordinator(headerMenu);

  headerMenu.deactivate = function (event) {
    scheduleClose(headerMenu, () => {
      originalDeactivate(event);
    });
  };

  headerMenu.activate = function (event) {
    cancelCloseTimer();
    cancelOpenTimer();

    const li = findListItemFromEventTarget(event.target);
    const dropdownSubmenu = li ? getDropdownSubmenu(li) : null;
    const { openMs } = getMenuDelays(headerMenu);

    const runActivate = () => {
      if (dropdownSubmenu) {
        normalizeDropdownPanel(headerMenu, dropdownSubmenu);
      }

      originalActivate.call(headerMenu, event);

      if (dropdownSubmenu?.hasAttribute('data-active')) {
        normalizeDropdownPanel(headerMenu, dropdownSubmenu);
        requestAnimationFrame(() => {
          normalizeDropdownPanel(headerMenu, dropdownSubmenu);
        });
      }
    };

    if (openMs > 0) {
      openTimer = window.setTimeout(runActivate, openMs);
    } else {
      runActivate();
    }
  };

  headerMenu.addEventListener(ThemeEvents.megaMenuHover, () => {
    cancelCloseTimer();
  });

  return true;
}

function tryPatch() {
  const headerMenu = document.querySelector(HEADER_MENU_SELECTOR);
  if (!headerMenu) return;

  if (patchHeaderMenu(headerMenu)) return;

  requestAnimationFrame(tryPatch);
}

function assertBlockNavMenuStyle() {
  setHeaderMenuStyle();
}

customElements.whenDefined('header-menu').then(() => {
  tryPatch();
  assertBlockNavMenuStyle();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    tryPatch();
    assertBlockNavMenuStyle();
  });
} else {
  tryPatch();
  assertBlockNavMenuStyle();
}

setTimeout(() => {
  tryPatch();
  assertBlockNavMenuStyle();
}, 1500);

// Breakpoint is configurable per header section (see sections/hw-header-2.liquid's
// "Desktop menu breakpoint" setting), so build the query from the live DOM value
// rather than a hardcoded 990px.
getHwBlockNavMediaQuery().addEventListener('change', assertBlockNavMenuStyle);
